import { atom } from 'jotai';

import { SettlementBillApi } from '@/api';
import { getAllQuery, getAmountTrueNum, getAmountNumber } from '@/utils';
import { BillType, IsEdit } from '@/config';
import { Omega } from '@/lib';
import {
  AdjustBillDetailApiRes,
  BillDetailOutParams,
  MainBillDetailApiRes,
  BillProductListParams
} from '@/types/index.d';

const { getBillDetail, getApprovalDetail, getAdjustAmountDetail, calculateBillAmount } =
  SettlementBillApi;

const defaultBillDetail: any = {
  bill_basic_info: null, // 账单基础信息
  bill_detail: null, // 账单详情
  bill_info: null, // 账单信息
  support_file: [] // 支持文件列表
};

const defaultApprovalDetail = {
  approval_node_list: [], // 审批节点列表
  approval_operate: [], // 审批操作列表
  current_approval_node: '' // 当前审批节点
};

const defaultOrderTable = {
  order_list: [], // 订单列表
  list_count: 0, // 订单列表数量
  adjust_order_count_threshold: 0 // 调整订单数量阈值
};

const defaultMissOrder = {
  missing_order_start_time: '', // 缺失订单开始时间
  missing_order_end_time: '', // 缺失订单结束时间
  missing_order_count: null, // 缺失订单数量
  missing_order_settlement_amount: '', // 缺失订单结算金额
  missing_order_document: null // 缺失订单凭证
};

const defaultRemark = {
  adjust_amount_comment: '', // 账单调整备注
  adjust_amount_document: null // 账单调整凭证
};

const defaultCalculateAmount = {
  settlement_amount: 'R$0,00', // 账单结算金额
  adjust_amount_diff: 'R$0,00', // 账单调整金额
  payment_amount: 'R$0,00', // 账单支付金额
  adjust_order_amount_diff: 'R$0,00', // 调整订单金额
  adjust_order_count: 0 // 调整订单数量
};

export const billDetailAtom = atom(defaultBillDetail);
export const approvalDetailAtom = atom(defaultApprovalDetail);
export const pageLoadingAtom = atom(true);
export const orderTableAtom = atom(defaultOrderTable);
export const missOrderAtom = atom(defaultMissOrder);
export const remarkAtom = atom(defaultRemark);
export const calculateAmountAtom = atom(defaultCalculateAmount); // 存储所有金额相关
export const orderTableLoadingAtom = atom(false);
export const amountDrawerLoadingAtom = atom(false);
export const isAmountDrawerTypeAtom = atom('');

// 添加分页状态原子
export const paginationAtom = atom({
  current: 1,
  pageSize: 5
});

// 添加表格数据原子，用于独立更新表格内容
export const tableDataAtom = atom<BillProductListParams[]>([]);

// 添加表格总数原子
export const tableCountAtom = atom<number>(0);

// 添加表格加载状态原子
export const tableLoadingAtom = atom<boolean>(false);

const tranAdjustDetail = (
  data: MainBillDetailApiRes | AdjustBillDetailApiRes
): BillDetailOutParams => {
  if (data?.bill_type !== BillType.AdjustBill) return data as MainBillDetailApiRes;

  const adjustData = data as AdjustBillDetailApiRes;

  const dataNew = {
    support_file: adjustData?.support_file,
    new_adjust_model: data?.new_adjust_model,
    bill_type: data?.bill_type,
    bill_basic_info: adjustData?.adjust_detail?.basic_info,
    bill_info: {
      gas_station_id: adjustData?.adjust_detail?.gas_station_id, // miss
      gas_station_name: adjustData?.adjust_detail?.gas_station_name, // miss
      bill_period: adjustData?.adjust_detail?.bill_period,
      cnpj: adjustData?.adjust_detail?.cnpj,
      bill_id: adjustData?.adjust_detail?.bill_id,
      bill_status: adjustData?.adjust_detail?.status, // status
      bill_origin_status: adjustData?.adjust_detail?.bill_origin_status, // bill_origin_status
      missing_info: adjustData?.adjust_detail?.missing_order_count > 0 && {
        list: [
          {
            range_time: `${adjustData?.adjust_detail?.missing_order_start_time} ~ ${adjustData?.adjust_detail?.missing_order_end_time}`,
            missing_order_document: adjustData?.adjust_detail?.missing_order_document?.[0],
            missing_order_count: adjustData?.adjust_detail?.missing_order_count,
            missing_order_settlement_amount:
              adjustData?.adjust_detail?.missing_order_settlement_amount?.[3]
          }
        ]
      },
      is_allow_adjust: adjustData?.adjust_detail?.is_allow_adjust
    },
    bill_detail: {
      adjust_amount: adjustData?.adjust_detail?.adjust_amount,
      adjust_item_info: adjustData?.adjust_detail?.adjust_item_info as any,
      finally_pay_amount: adjustData?.adjust_detail?.finally_pay_amount,
      openning_balance: adjustData?.adjust_detail?.openning_balance,
      current_period_amount: adjustData?.adjust_detail?.adjust_amount,
      closing_balance: adjustData?.adjust_detail?.closing_balance
    } as any,
    adjust_detail: {
      ...adjustData?.adjust_detail,
      adjust_orders: adjustData?.adjust_detail?.adjust_orders?.map((item: any) => {
        return {
          ...item,
          pay_amount: getAmountNumber(item?.pay_amount?.[3]),
          didi_discount: getAmountNumber(item?.didi_discount?.[3]),
          original_price_total: getAmountNumber(item?.original_price_total?.[3]),
          original_price_unit: getAmountNumber(item?.original_price_unit?.[3]),
          receivable_amount: getAmountNumber(item?.receivable_amount?.[3]),
          station_discount_total: getAmountNumber(item?.station_discount_total?.[3]),
          station_discount_unit: getAmountNumber(item?.station_discount_unit?.[3]),
          tr_amount: getAmountNumber(item?.tr_amount?.[3]),
          station_expected_amount: getAmountNumber(item?.station_expected_amount?.[3]),
          discount_price: getAmountNumber(item?.discount_price?.[3])
        };
      })
    }
  };

  return dataNew;
};

/**
 * 刷新页面
 */
export const setRefreshPage = atom(
  null,
  async (
    get,
    set,
    {
      bill_id,
      pagination
    }: {
      bill_id: number;
      pagination?: {
        current: number;
        pageSize: number;
      };
    }
  ) => {
    try {
      const urlParams: any = getAllQuery();
      const { current = 1, pageSize = 5 } = pagination || {};

      set(pageLoadingAtom, true);
      const [billDetailItem, approvalDetailItem]: any = await Promise.all([
        getBillDetail({
          bill_id: bill_id || urlParams?.billId,
          page: current,
          size: pageSize
        }),
        getApprovalDetail({ bill_id: bill_id || urlParams?.billId })
      ]);

      set(
        billDetailAtom,
        tranAdjustDetail(billDetailItem?.data as BillDetailOutParams) || defaultBillDetail
      );
      set(approvalDetailAtom, approvalDetailItem?.data || defaultApprovalDetail);
    } catch (error) {
      console.error('加载页面失败', error);
      set(billDetailAtom, defaultBillDetail);
      set(approvalDetailAtom, defaultApprovalDetail);
    } finally {
      set(pageLoadingAtom, false);
    }
  }
);

/**
 * 重置页面
 */
export const resetPage = atom(null, (get, set) => {
  set(billDetailAtom, defaultBillDetail);
  set(approvalDetailAtom, defaultApprovalDetail);
});

/**
 * 获取缺失订单数据
 */
const getMissOrderData = (data: any, isAmountDrawerType: string) => {
  const missOrderData = data
    ? {
        missing_order_start_time: data?.missing_order_start_time,
        missing_order_end_time: data?.missing_order_end_time,
        missing_order_count: data?.missing_order_count || null,
        missing_order_settlement_amount:
          getAmountTrueNum(data?.missing_order_settlement_amount?.[3]) === 0
            ? ''
            : getAmountNumber(data?.missing_order_settlement_amount?.[3]),
        missing_order_document: data?.missing_order_document
      }
    : defaultMissOrder;

  Omega.trackBusiness('tech_ibt_gd_gas_station_business', {
    business: 'misOrder',
    type: 'getMissOrderData',
    status: isAmountDrawerType,
    msg: '',
    params: { missing_order_document: missOrderData?.missing_order_document }
  });
  return missOrderData;
};

/**
 * 获取订单table数据
 */
const getOrderTableData = (data: any) => {
  const orderTableData = data
    ? {
        order_list: data?.order_list?.map((item: any) => {
          return {
            ...item,
            didi_discount: getAmountNumber(item?.didi_discount?.[3]),
            discount_price: getAmountNumber(item?.discount_price?.[3]),
            original_price_total: getAmountNumber(item?.original_price_total?.[3]),
            original_price_unit: getAmountNumber(item?.original_price_unit?.[3]),
            receivable_amount: getAmountNumber(item?.receivable_amount?.[3]),
            station_discount_total: getAmountNumber(item?.station_discount_total?.[3]),
            station_discount_unit: getAmountNumber(item?.station_discount_unit?.[3]),
            tr_amount: getAmountNumber(item?.tr_amount?.[3]),
            pay_amount: getAmountNumber(item?.pay_amount?.[3]),
            station_expected_amount:
              item?.is_edit === IsEdit.Edit
                ? getAmountNumber(item?.station_expected_amount?.[3])
                : null
          };
        }),
        list_count: data?.list_count,
        adjust_order_count_threshold: data?.adjust_order_count_threshold
      }
    : defaultOrderTable;

  return orderTableData;
};

/**
 * 获取备注数据
 */
const getRemarkData = (data: any) => {
  const reamarkData = data
    ? {
        adjust_amount_comment: data?.adjust_amount_comment,
        adjust_amount_document: data?.adjust_amount_document
      }
    : defaultRemark;

  return reamarkData;
};

/**
 * 获取计算数据
 */
const getCalculateData = (data: any) => {
  const calculateData = data
    ? {
        settlement_amount: data?.settlement_amount?.[3],
        adjust_amount_diff: data?.adjust_amount_diff?.[3],
        payment_amount: data?.payment_amount?.[3],
        adjust_order_amount_diff: data?.adjust_order_amount_diff?.[3],
        adjust_order_count: data?.adjust_order_count
      }
    : defaultCalculateAmount;

  return calculateData;
};

/**
 * 获取缺失订单金额
 */
const getMissAmount = (data: any) => {
  const missingOrderSettlementAmount = getAmountTrueNum(data?.missing_order_settlement_amount?.[3]);

  return missingOrderSettlementAmount;
};

/**
 * 获取账单调整金额详情
 */
export const setAdjustAmountDetail = atom(null, async (get, set, payload: any) => {
  try {
    const urlParams: any = getAllQuery();

    set(amountDrawerLoadingAtom, true);

    const adjustAmountDetail = await getAdjustAmountDetail({ ...payload });

    const missingOrderSettlementAmount = getMissAmount(adjustAmountDetail?.data);

    const calculateAmount = await calculateBillAmount({
      bill_id: payload.bill_id || urlParams?.billId,
      missing_order_settlement_amount: missingOrderSettlementAmount
    });
    const isAmountDrawerType = get(isAmountDrawerTypeAtom);

    set(orderTableAtom, getOrderTableData(adjustAmountDetail?.data));
    set(missOrderAtom, getMissOrderData(adjustAmountDetail?.data, isAmountDrawerType));
    set(remarkAtom, getRemarkData(adjustAmountDetail?.data));
    set(calculateAmountAtom, getCalculateData(calculateAmount?.data));
  } catch (error) {
    set(orderTableAtom, defaultOrderTable);
    set(missOrderAtom, defaultMissOrder);
    set(remarkAtom, defaultRemark);
    set(calculateAmountAtom, defaultCalculateAmount);
    console.error('获取账单调整金额详情失败：', error);
  } finally {
    set(amountDrawerLoadingAtom, false);
  }
});

/**
 * 获取订单table
 */
export const setOrderTable = atom(null, async (get, set, payload: any) => {
  try {
    set(orderTableLoadingAtom, true);
    const response: any = await getAdjustAmountDetail({
      ...payload
    });

    set(orderTableAtom, getOrderTableData(response?.data));
  } catch (error) {
    set(orderTableAtom, defaultOrderTable);
    console.error('获取订单table失败：', error);
  } finally {
    set(orderTableLoadingAtom, false);
  }
});

/**
 * 计算账单金额
 */
export const toCalculateBillAmount = atom(null, async (get, set, payload: any) => {
  try {
    const response: any = await calculateBillAmount({
      ...payload
    });

    set(calculateAmountAtom, getCalculateData(response?.data));
  } catch (error) {
    set(calculateAmountAtom, defaultCalculateAmount);
    console.error('获取账单调整金额详情失败：', error);
  }
});

/**
 * 获取表格数据（分页）
 */
export const fetchTableData = atom(
  null,
  async (
    get,
    set,
    {
      bill_id,
      pagination
    }: {
      bill_id: number;
      pagination: {
        current: number;
        pageSize: number;
      };
    }
  ) => {
    try {
      const urlParams: any = getAllQuery();
      const { current = 1, pageSize = 5 } = pagination;

      set(tableLoadingAtom, true);

      // 只获取表格数据，不获取其他数据
      const billDetailItem = await getBillDetail({
        bill_id: bill_id || urlParams?.billId,
        page: current,
        size: pageSize
      });

      const transformedData = tranAdjustDetail(billDetailItem?.data);

      // 只更新表格相关的数据
      set(tableDataAtom, (transformedData?.adjust_detail as any)?.adjust_orders || []);
      set(tableCountAtom, transformedData?.adjust_detail?.list_count || 0);
    } catch (error) {
      console.error('加载表格数据失败', error);
      set(tableDataAtom, []);
      set(tableCountAtom, 0);
    } finally {
      set(tableLoadingAtom, false);
    }
  }
);
